// Shaping the PR flow series for the chart.
//
// Separate from the component so the awkward part can be tested. A week with no
// PR events at all is not a quiet week, it is an absence of data - usually
// ingestor downtime - and the chart says so rather than drawing a bar of zero.

export interface PrFlowWeek {
  bucket_start: string;
  opened: number;
  closed: number;
  net: number;
  cumulative: number;
  silent: boolean;
}


/** How many weeks in the window recorded nothing. Surfaced next to the chart so
 *  a flat stretch is not mistaken for a calm one. */
export function silentWeekCount(weeks: PrFlowWeek[]): number {
  return weeks.filter((w) => w.silent).length;
}

// ---------------------------------------------------------------------------
// Day of week
// ---------------------------------------------------------------------------
//
// Two letters, not one: T and S each appear twice in a week, so single letters
// are ambiguous exactly where it matters most. Mo Tu We Th Fr Sa Su.
//
// PARSED AS UTC, deliberately. The API sends 'YYYY-MM-DD', which JavaScript
// reads as UTC midnight. Calling getDay() then uses the LOCAL offset, so west of
// Greenwich a Saturday becomes a Friday and the labels are quietly wrong for
// half the world. getUTCDay() is the only correct read here.

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;
// Single letters, for windows too narrow to fit two. T and S each appear twice,
// which is acceptable here and not in the short view: in a CONTIGUOUS run of
// days the position disambiguates - a T between Mo and W can only be Tuesday -
// whereas at 14 days a reader is more likely to look one up on its own.
const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

/** Two-letter weekday for a 'YYYY-MM-DD' bucket. Empty string if unparseable. */
export function weekdayLabel(isoDate: string): string {
  const t = Date.parse(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(t)) return '';
  return WEEKDAY_LABELS[new Date(t).getUTCDay()] ?? '';
}

/** Saturday or Sunday, in UTC, for the same reason as above. */
export function isWeekend(isoDate: string): boolean {
  const t = Date.parse(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(t)) return false;
  const d = new Date(t).getUTCDay();
  return d === 0 || d === 6;
}

/** One-letter weekday for a 'YYYY-MM-DD' bucket. Same UTC rule as above. */
export function weekdayLetter(isoDate: string): string {
  const t = Date.parse(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(t)) return '';
  return WEEKDAY_LETTERS[new Date(t).getUTCDay()] ?? '';
}
